import type React from 'react';
import { useState } from 'react';
import { Space, Row, Col, Tabs } from 'antd';
import { useHistory } from 'react-router-dom';
import { RowGutter } from 'common/spacing';
import { Card, Button, PageHeader } from 'components';
import { MeterEventEntity } from '@enmon/commons/lib/tools';
import { GenerateApiKeysButton } from './components/GenerateApiKeysButton';
import { DemoAccountCard } from './demo-account/Card';
import { InventoryMetersTable } from './components/InventoryMetersTable';
import { AsyncJobsTable } from './components/AsyncJobsTable';
import { MeterEventsTable } from './components/MeterEventsTable';
import { MetersCardTab } from './enums/MeterCardTab';
import { JobsCardTab } from './enums/JobsCardTab';

const { TabPane } = Tabs;

const METERS_CARD_TAB_TITLE: Record<MetersCardTab, string> = {
  [MetersCardTab.Pasport]: 'Pasported',
  [MetersCardTab.Update]: 'Updated',
  [MetersCardTab.Replace]: 'Replaced',
  [MetersCardTab.Install]: 'Installed',
  [MetersCardTab.Service]: 'Serviced',
};

const JOBS_CARD_TAB_TITLE: Record<JobsCardTab, string> = {
  [JobsCardTab.MeterActivation]: 'Activate meters',
  [JobsCardTab.MeterDeactivation]: 'Deactivate meters',
  [JobsCardTab.PDFPasport]: 'PDF pasports',
  [JobsCardTab.ParseDocuments]: 'Parse documents',
  [JobsCardTab.ProcessInvoices]: 'Process invoices',
};

const METER_EVENTS_CARD_TAB_TITLE: Record<MeterEventEntity, string> = {
  [MeterEventEntity.BackendGatewayMeter]: 'Gateway',
  [MeterEventEntity.InventoryMeter]: 'Inventory',
};

export const Dashboard: React.VFC = () => {
  const history = useHistory();
  const [activeMetersTab, setActiveMetersTab] = useState(MetersCardTab.Pasport);
  const [activeJobsTab, setActiveJobsTab] = useState(JobsCardTab.MeterActivation);
  const [activeMeterEventsTab, setActiveMeterEventsTab] = useState(MeterEventEntity.InventoryMeter);

  return (
    <Space direction="vertical" style={{ display: 'flex' }}>
      <PageHeader breadcrumbs={[]} extra={<GenerateApiKeysButton />} ghost={false} title="Dashboard" />
      <Row gutter={RowGutter.Default}>
        <Col lg={12} md={24} xl={6}>
          <Card title="Customers">
            <Button size="large" type="primary" onClick={() => history.push(`/portfolio/customers/create`)}>
              Create new
            </Button>
          </Card>
        </Col>
        <Col lg={12} md={24} xl={6}>
          <DemoAccountCard />
        </Col>
      </Row>
      <Row gutter={RowGutter.Default}>
        <Col lg={24} xl={12}>
          <Card title="Inventory Meters">
            <Tabs activeKey={activeMetersTab} onTabClick={(activeTab: MetersCardTab) => setActiveMetersTab(activeTab)}>
              {Object.values(MetersCardTab).map(tab => (
                <TabPane key={tab} tab={METERS_CARD_TAB_TITLE[tab]}>
                  <InventoryMetersTable activeMetersTab={activeMetersTab} />
                </TabPane>
              ))}
            </Tabs>
          </Card>
        </Col>
        <Col lg={24} xl={12}>
          <Card title="Latest jobs">
            <Tabs activeKey={activeJobsTab} onTabClick={(activeTab: JobsCardTab) => setActiveJobsTab(activeTab)}>
              {Object.values(JobsCardTab).map(tab => (
                <TabPane key={tab} tab={JOBS_CARD_TAB_TITLE[tab]}>
                  <AsyncJobsTable activeJobsTab={activeJobsTab} />
                </TabPane>
              ))}
            </Tabs>
          </Card>
        </Col>
      </Row>
      <Row gutter={RowGutter.Default}>
        <Col lg={24} xl={12}>
          <Card title="Meter Events">
            <Tabs
              activeKey={activeMeterEventsTab}
              onTabClick={(activeTab: MeterEventEntity) => setActiveMeterEventsTab(activeTab)}
            >
              {Object.values(MeterEventEntity).map(tab => (
                <TabPane key={tab} tab={METER_EVENTS_CARD_TAB_TITLE[tab]}>
                  <MeterEventsTable activeMeterEventsTab={activeMeterEventsTab} />
                </TabPane>
              ))}
            </Tabs>
          </Card>
        </Col>
      </Row>
    </Space>
  );
};
